import { Dispatch, SetStateAction, useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { RecurringExportContextValue } from 'contexts/recurringExportContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { usePostReportScheduleMutation } from 'hooks/mutations/usePostReportScheduleMutation'
import { usePutReportScheduleMutation } from 'hooks/mutations/usePutReportScheduleMutation'
import { BaseErrorResponse } from 'types/beneficiaries'
import {
  ExportFileFormat,
  ExportTabItems,
  ExportTabItemsType,
  RecurringExportForm,
  RecurringExportSteps,
  RecurringExportStepsType,
  ReportSchedule,
  ReportScheduleFrequency,
  ReportTypes,
} from 'types/transactionsExport'
import {
  buildBaseAmplitudeProperties,
  handleScheduleError,
  handleScheduleSuccess,
} from 'utils/export/exportHandlers'

interface UseRecurringTransactionsExportProps {
  handleClose: () => void
  setOpenTab: Dispatch<SetStateAction<ExportTabItemsType>>
}

export const useRecurringTransactionsExport = ({
  handleClose,
  setOpenTab,
}: UseRecurringTransactionsExportProps): RecurringExportContextValue => {
  const { t } = useTranslation()
  const { profile } = useProfileContext()

  const {
    mutate: postReportSchedule,
    isPending: isLoadingPostReportSchedule,
    error: postReportScheduleError,
    isError: isErrorPostReportSchedule,
  } = usePostReportScheduleMutation()

  const {
    mutate: putReportSchedule,
    isPending: isLoadingPutReportSchedule,
    error: putReportScheduleError,
    isError: isErrorPutReportSchedule,
  } = usePutReportScheduleMutation()

  const [currentReportSchedule, setCurrentReportSchedule] = useState<ReportSchedule>(null)
  const [step, setStep] = useState<RecurringExportStepsType>(RecurringExportSteps.LIST)
  const [isUpdatingReportSchedule, setIsUpdatingReportSchedule] = useState(false)

  const defaultValues: RecurringExportForm = {
    frequencyType: ReportScheduleFrequency.DAILY,
    format: ExportFileFormat?.CSV,
    frequencyDay: '1',
    time: '09:00',
    timezone: null,
  }

  const form = useForm({
    defaultValues,
    mode: 'onChange',
  })

  const { control, setError } = form

  const formValues = useWatch({ control }) as RecurringExportForm

  // Build amplitude properties using shared utilities
  const amplitudeProperties = buildBaseAmplitudeProperties({
    type: ExportTabItems.RECURRING,
    frequencyType: formValues?.frequencyType,
    ...(Number(formValues?.frequencyDay) && {
      frequencyDay: Number(formValues?.frequencyDay),
    }),
    time: formValues?.time,
    timezone: formValues?.timezone,
  })

  /**
   * Create new report schedule
   * Uses shared success/error handlers
   */
  const createReportSchedule: SubmitHandler<RecurringExportForm> = () => {
    if (!formValues?.time) {
      setError('time', { type: 'custom', message: t('exports.timeRequiredField') })
      return
    }
    if (!formValues?.timezone) {
      setError('timezone', { type: 'custom', message: t('exports.timezoneRequiredField') })
      return
    }

    const reportScheduleData = {
      ...formValues,
      channel: 'email',
      frequencyDay: Number(formValues?.frequencyDay),
      email: profile?.emailAddress,
      reportParams: {
        reportType: ReportTypes.TRANSACTION,
        walletId: undefined,
      },
    }

    setCurrentReportSchedule(reportScheduleData)

    postReportSchedule(reportScheduleData, {
      onSuccess: () => {
        const customMessage = `${t('exports.daily')} ${t('exports.at')} ${reportScheduleData?.time} ${reportScheduleData?.timezone}, ${reportScheduleData?.format?.toUpperCase()}`
        handleScheduleSuccess(amplitudeProperties, t, handleClose, false, customMessage)
      },
      onError: (error: BaseErrorResponse) => {
        handleScheduleError(error, amplitudeProperties, false)
      },
    })
  }

  const updateReportSchedule: SubmitHandler<RecurringExportForm> = () => {
    const reportScheduleData = {
      ...currentReportSchedule,
      ...formValues,
      frequencyDay: Number(formValues?.frequencyDay),
      reportParams: {
        reportType: ReportTypes.TRANSACTION,
        walletId: currentReportSchedule?.reportParams?.walletId,
      },
    }

    setCurrentReportSchedule(reportScheduleData)

    putReportSchedule(reportScheduleData, {
      onSuccess: () => {
        const customMessage = `${t('exports.daily')} ${t('exports.at')} ${reportScheduleData?.time} ${reportScheduleData?.timezone}, ${reportScheduleData?.format?.toUpperCase()}`
        handleScheduleSuccess(amplitudeProperties, t, handleClose, true, customMessage)
      },
      onError: (error: BaseErrorResponse) => {
        handleScheduleError(error, amplitudeProperties, true)
      },
    })
  }

  return {
    step,
    setStep,
    handleClose,
    currentReportSchedule,
    setCurrentReportSchedule,
    isUpdatingReportSchedule,
    setIsUpdatingReportSchedule,
    form,
    createReportSchedule,
    updateReportSchedule,
    setOpenTab,
    isLoadingPostReportSchedule,
    isErrorPostReportSchedule,
    postReportScheduleError,
    isLoadingPutReportSchedule,
    isErrorPutReportSchedule,
    putReportScheduleError,
  }
}
